/**
 * Workshop W2 (Plan 2026-10-29-workshop-luecken, Paket P): „Wenn ein Webinar wiederholt wird". Je Webinar mit mehreren
 * Läufen (gleiches Konzept-Kürzel oder gleicher öffentlicher Titel, `gruppiereWebinare` — wie die Webinar-Tabelle):
 * Anmeldungen je 1.000 FT-Kontakte je Lauf, Pause seit dem vorigen Lauf, Klick → Anmeldung als Wahlspalte. Kleine Linien
 * nur für Webinare mit mindestens drei Läufen mit FT-Daten, KEIN Durchschnitt über die Webinare (jedes hat eigene Pausen,
 * Listen und Jahreszeiten). Ersetzt das Deck-Format „Klick → Anmeldung je Lauf derselben Anmeldeseite" (seit der
 * LP-Migration hat jeder Termin eine eigene Seite). Backend `services/auswertungen/wiederholungService.ts`.
 */
import { FC, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Link, useTheme } from '@mui/material';
import { ResponsiveLine } from '@nivo/line';
import ChartCard from '../../../components/auswertungen/ChartCard';
import SortableTable, { Spalte, spaltenMeta } from '../../../components/auswertungen/SortableTable';
import SpaltenMenue from '../../../components/auswertungen/SpaltenMenue';
import { Breite, useSpaltenwahl } from '../../../components/auswertungen/spaltenwahl';
import { BefundZeile, jeTausend, kompakt, nachGruppenReihenfolge } from '../../../components/auswertungen/kennzahlTeile';
import { runderHoechstwert, TipRow, UNSICHER, useVizStyle } from '../../../components/auswertungen/vizStyle';
import { formatPct, TERMS } from '../../../helpers/kpiDefinitions';
import { fetchWiederholungen, WiederholungGruppe, WiederholungLauf } from '../../../helpers/auswertungenData';
import { Hinweis, int, Laden, Strich, tagMitWochentag, Unter, useLaden } from './shared';

type Zeile = WiederholungLauf & { gruppe: WiederholungGruppe };

const GRUPPEN = ['Lauf', 'Anmeldung'] as const;
const STANDARDS: Record<Breite, readonly string[]> = {
  breit: ['webinar', 'lauf', 'datum', 'pause', 'anm1k'],
  mittel: ['webinar', 'lauf', 'pause', 'anm1k'],
  schmal: ['webinar', 'lauf', 'anm1k'],
};

/** „3" bzw. „mind. 3.", wenn der erste bekannte Lauf vor dem Beginn der Kontaktdaten liegt (Audit D1: Wort statt „≥"). */
export const laufText = (l: Pick<WiederholungLauf, 'lauf' | 'lauf_mindestens'>): string => (l.lauf_mindestens ? `mind. ${l.lauf}.` : `${l.lauf}`);

/**
 * Punkte einer kleinen Linie auf einem LINEAREN Lauf-Index (Audit D1): vom ersten bis zum letzten Lauf mit FT-Daten
 * jede Laufnummer; ein Lauf ohne Wert (oder eine fehlende Nummer) steht als `y: null` und unterbricht die Linie.
 */
export function linienPunkte(laeufe: WiederholungLauf[]): { x: number; y: number | null; l: WiederholungLauf | null }[] {
  const mit = laeufe.filter((l) => l.hat_daten && l.anmeldungen_je_1k !== null);
  if (!mit.length) return [];
  const je = new Map(laeufe.map((l) => [l.lauf, l]));
  const von = Math.min(...mit.map((l) => l.lauf));
  const bis = Math.max(...mit.map((l) => l.lauf));
  const out: { x: number; y: number | null; l: WiederholungLauf | null }[] = [];
  for (let n = von; n <= bis; n += 1) {
    const l = je.get(n) ?? null;
    out.push({ x: n, y: l && l.hat_daten && l.anmeldungen_je_1k !== null ? l.anmeldungen_je_1k : null, l });
  }
  return out;
}

/**
 * Befund (rein): bei wie vielen Webinaren mit Linie liegt der letzte Lauf mit FT-Daten unter dem ersten — ohne einen
 * Durchschnitt über die Webinare zu bilden.
 */
export function wiederholungBefund(gruppen: WiederholungGruppe[]): string | null {
  const linien = gruppen.filter((g) => g.linie);
  if (!linien.length) return null;
  let tiefer = 0;
  for (const g of linien) {
    const mit = g.laeufe.filter((l) => l.hat_daten && l.anmeldungen_je_1k !== null);
    if ((mit[mit.length - 1].anmeldungen_je_1k as number) < (mit[0].anmeldungen_je_1k as number)) tiefer += 1;
  }
  const n = linien.length;
  return `${n} ${n === 1 ? 'Webinar lief' : 'Webinare liefen'} mindestens dreimal mit FT-Daten; bei ${tiefer} davon brachte der letzte Lauf weniger Anmeldungen je 1.000 Kontakte als der erste.`;
}

/** Eine kleine Linie; `skala` ist für ALLE Felder dieselbe (D1), Gitter = Achsenticks. Graue Läufe hohl (`UNSICHER`). */
const Linie: FC<{ g: WiederholungGruppe; skala: { max: number; ticks: number[] } }> = ({ g, skala }) => {
  const viz = useVizStyle();
  const theme = useTheme();
  const punkte = linienPunkte(g.laeufe);
  const mitDaten = punkte.filter((p) => p.y !== null).length;
  const data = [{ id: g.group_key, data: punkte }];
  const xs = punkte.map((p) => p.x);
  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: '8px', p: 1, minWidth: 0 }} data-grafik="wiederholung">
      <Box sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={g.title}>
        {g.title}
      </Box>
      <Box sx={{ fontSize: 11, color: 'text.secondary' }}>
        {g.plx_product ?? 'ohne Produkt'} · {int(mitDaten)} Läufe mit FT-Daten{g.erster_lauf_vor_daten ? ' · erster Lauf vor Datenbeginn' : ''}
      </Box>
      <Box sx={{ height: 150 }}>
        <ResponsiveLine
          data={data}
          colors={[viz.series1]}
          margin={{ top: 12, right: 16, bottom: 28, left: 36 }}
          xScale={{ type: 'linear', min: Math.min(...xs), max: Math.max(...xs) }}
          yScale={{ type: 'linear', min: 0, max: skala.max }}
          theme={viz.nivo}
          animate={false}
          lineWidth={2}
          pointSize={7}
          pointColor={{ from: 'color' }}
          pointSymbol={({ size, color, datum }) => {
            const grau = Boolean((datum as unknown as { l: WiederholungLauf | null }).l?.grau);
            return (
              <circle
                r={size / 2}
                fill={grau ? theme.palette.background.default : color}
                stroke={color}
                strokeWidth={grau ? UNSICHER.kontur : 0}
                data-unsicher={grau ? 'kontur' : undefined}
              />
            );
          }}
          enableGridX={false}
          gridYValues={skala.ticks}
          axisLeft={{ tickSize: 0, tickPadding: 4, tickValues: skala.ticks, format: (v) => Number(v).toLocaleString('de-DE', { maximumFractionDigits: 1 }) }}
          axisBottom={{ tickSize: 0, tickPadding: 6, tickValues: xs, format: (v) => String(v) }}
          useMesh
          tooltip={({ point }) => {
            const l = (point.data as unknown as { l: WiederholungLauf | null }).l;
            if (!l) return null;
            return (
              <Box>
                <Box sx={{ fontWeight: 600, mb: 0.5 }}>
                  Lauf {laufText(l)} · {tagMitWochentag(l.start_date)}
                </Box>
                <TipRow color={viz.series1} label={TERMS.anmeldungenJe1kFt.short} value={jeTausend(l.anmeldungen_je_1k)} />
                <TipRow label="Anmeldungen über FT" value={int(l.anmeldungen)} />
                <TipRow label="FT-Kontakte" value={kompakt(l.contacts)} />
                {l.pause_tage !== null && <TipRow label="Pause" value={`${int(l.pause_tage)} Tage`} />}
              </Box>
            );
          }}
          role="img"
        />
      </Box>
    </Box>
  );
};

const WiederholungKarte: FC = () => {
  const { data, fehler } = useLaden(fetchWiederholungen);
  const zeilen: Zeile[] = useMemo(() => (data?.gruppen ?? []).flatMap((g) => g.laeufe.map((l) => ({ ...l, gruppe: g }))), [data]);
  const linien = useMemo(() => (data?.gruppen ?? []).filter((g) => g.linie), [data]);
  // D1: EINE y-Skala für alle Felder — sonst sieht 2 je 1.000 im einen Feld so hoch aus wie 8 im anderen.
  const skala = useMemo(() => runderHoechstwert(Math.max(0, ...linien.flatMap((g) => linienPunkte(g.laeufe).map((p) => p.y ?? 0))), 3), [linien]);
  const minAnm = data?.min_anmeldungen ?? 20;
  // Sortierschlüssel „Webinar": Gruppe (jüngster Titel) und darin der Lauf — so stehen die Läufe eines Webinars beisammen.
  const gruppenRang = useMemo(() => new Map((data?.gruppen ?? []).map((g, i) => [g.group_key, i])), [data]);

  const baue = (): Spalte<Zeile>[] => [
    {
      key: 'webinar',
      label: 'Webinar',
      tip: 'Titel des Laufs; die Läufe eines Webinars stehen beim Sortieren nach dieser Spalte beisammen (in der Reihenfolge der Karte).',
      gruppe: 'Lauf',
      pflicht: true,
      render: (r) => (
        <Box sx={{ lineHeight: 1.3, maxWidth: 300 }}>
          <Link component={RouterLink} to={`/auswertungen/webinare/${r.webinar_id}`} underline="hover" sx={{ fontWeight: 600 }}>
            {r.title}
          </Link>
          <Box component="span" sx={{ display: 'block', fontSize: 12, color: 'text.secondary' }}>
            {r.plx_product ?? '–'} · {tagMitWochentag(r.start_date)}
          </Box>
        </Box>
      ),
      sortValue: (r) => `${String(gruppenRang.get(r.gruppe.group_key) ?? 999).padStart(3, '0')}-${String(r.lauf).padStart(3, '0')}`,
    },
    {
      key: 'lauf',
      label: TERMS.laufNummer.label,
      tip: TERMS.laufNummer.tip,
      gruppe: 'Lauf',
      pflicht: true,
      align: 'right',
      render: (r) => laufText(r),
      sortValue: (r) => r.lauf,
    },
    {
      key: 'datum',
      label: 'Datum',
      gruppe: 'Lauf',
      align: 'right',
      render: (r) => tagMitWochentag(r.start_date),
      sortValue: (r) => r.start_date,
    },
    {
      key: 'pause',
      label: TERMS.pauseTage.label,
      einheit: TERMS.pauseTage.einheit,
      tip: TERMS.pauseTage.tip,
      gruppe: 'Lauf',
      align: 'right',
      render: (r) => (r.pause_tage === null ? <Strich grund="Erster bekannter Lauf." /> : `${int(r.pause_tage)} Tage`),
      sortValue: (r) => r.pause_tage,
    },
    {
      key: 'anm1k',
      label: TERMS.anmeldungenJe1kFt.label,
      tip: TERMS.anmeldungenJe1kFt.tip,
      gruppe: 'Anmeldung',
      pflicht: true,
      align: 'right',
      unsicher: (r) => (r.grau ? `unter ${minAnm} Anmeldungen` : null),
      render: (r) =>
        r.anmeldungen_je_1k === null ? (
          <Strich grund={r.start_date.slice(0, 10) < (data?.daten_ab ?? '') ? 'Vor dem Beginn der Kontaktdaten.' : 'Keine Finanztrends-Einladung mit Empfängerzahl.'} />
        ) : (
          <Box sx={{ lineHeight: 1.25 }}>
            {jeTausend(r.anmeldungen_je_1k)}
            <Unter>
              {int(r.anmeldungen)} von {kompakt(r.contacts)}
            </Unter>
          </Box>
        ),
      sortValue: (r) => r.anmeldungen_je_1k,
    },
    {
      key: 'klick',
      label: TERMS.klickZuAnmeldung.label,
      einheit: 'nur FT',
      tip: TERMS.klickZuAnmeldung.tip,
      gruppe: 'Anmeldung',
      align: 'right',
      unsicher: (r) => (r.grau ? `unter ${minAnm} Anmeldungen` : null),
      render: (r) =>
        r.klick_zu_anmeldung === null ? (
          '–'
        ) : (
          <Box sx={{ lineHeight: 1.25 }}>
            {formatPct(r.klick_zu_anmeldung, 1)}
            <Unter>{int(r.klicks)} Klicker</Unter>
          </Box>
        ),
      sortValue: (r) => r.klick_zu_anmeldung,
    },
  ];
  const meta = nachGruppenReihenfolge(spaltenMeta(baue()), GRUPPEN);
  const wahl = useSpaltenwahl('wiederholungen', meta, STANDARDS);
  const sichtbar = new Set(wahl.sichtbar);
  const spalten = baue().filter((c) => sichtbar.has(c.key));
  const befund = data ? wiederholungBefund(data.gruppen) : null;
  const datenAb = data?.daten_ab ? data.daten_ab.split('-').reverse().join('.') : '–';

  return (
    <ChartCard
      title="Wenn ein Webinar wiederholt wird"
      actions={<SpaltenMenue spalten={meta} wahl={wahl} />}
      howToRead="Je Webinar, das mehrmals lief: Wie viele Personen meldeten sich je 1.000 Finanztrends-Kontakte an — beim ersten Lauf, beim zweiten, beim dritten? Jede Linie ist ein Webinar für sich. Ein Durchschnitt über alle Webinare fehlt bewusst: Pausen, Listen und Jahreszeit sind je Webinar verschieden."
      terms={[
        { label: TERMS.anmeldungenJe1kFt.label, tip: TERMS.anmeldungenJe1kFt.tip },
        { label: TERMS.laufNummer.label, tip: TERMS.laufNummer.tip },
        { label: TERMS.pauseTage.label, tip: TERMS.pauseTage.tip },
        { label: TERMS.klickZuAnmeldung.label, tip: TERMS.klickZuAnmeldung.tip },
      ]}
      source="Webinar-Termine, Finanztrends-Einladungen (Versand-Cache: Kontakte, Klicker, Anmeldungen je Versand)"
      freshnessKey={['webinar_send_metrics']}
    >
      <Box ref={wahl.messRef}>
        {!data ? (
          <Laden fehler={fehler} />
        ) : (
          <>
            <BefundZeile text={befund} fehlt={befund ? null : 'Noch kein Webinar lief dreimal mit Finanztrends-Kontaktdaten.'} tip={TERMS.anmeldungenJe1kFt.tip} />
            {linien.length > 0 && (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5, mb: 2 }}>
                {linien.map((g) => (
                  <Linie key={g.group_key} g={g} skala={skala} />
                ))}
              </Box>
            )}
            <SortableTable
              rows={zeilen}
              columns={spalten}
              rowKey={(r) => r.webinar_id}
              initialSort={{ key: 'webinar', dir: 'asc' }}
              maxHeight={null}
              eng={wahl.breite !== 'breit'}
            />
            <Hinweis>
              Anmeldungen = Personen mit Live-Anmeldung über eine Finanztrends-Einladung, je Person einmal. Kontaktdaten je Versand gibt es ab{' '}
              {datenAb}; ältere Läufe stehen ohne Wert da. „mind.“ vor der Laufnummer: der erste bekannte Lauf liegt vor diesem Datum, frühere Läufe
              mit anderem Titel können fehlen. Grau = unter {minAnm} Anmeldungen. Wiederholungen einer Aufzeichnung an Wochentagen sind keine neuen
              Läufe und fehlen hier. Spätere Läufe liegen auch später im Jahr: Ein Rückgang kann also zum Teil der allgemeine Trend sein
              (Anmeldungen und Käufe ändern sich über die Zeit), nicht nur ein abgenutztes Thema. Gezählt über {int(data.termine)} gelaufene Termine.
              {data.stichtag_hinweis && ` ${data.stichtag_hinweis}`}
            </Hinweis>
          </>
        )}
      </Box>
    </ChartCard>
  );
};

export default WiederholungKarte;
